import type { ShareRef, Stop, TripState } from "./types";

const STORAGE_KEY = "tripPlanner.v1";

function isShareRef(v: unknown): v is ShareRef {
  const s = v as ShareRef | undefined;
  return !!s && typeof s.id === "string" && !!s.id && typeof s.editToken === "string" && !!s.editToken;
}

export type StopChangeDetail =
  | { kind: "add"; stop: Stop }
  | { kind: "update"; stop: Stop }
  | { kind: "remove"; id: string }
  | { kind: "reorder" }
  | { kind: "load" }
  | { kind: "rename" };

function newTripState(): TripState {
  return { id: crypto.randomUUID(), createdAt: new Date().toISOString(), stops: [] };
}

class StopsStore extends EventTarget {
  private state: TripState = newTripState();

  load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as TripState;
        if (Array.isArray(parsed?.stops)) {
          this.state = {
            // Migrate legacy trips that predate id/createdAt: mint them once now.
            id: typeof parsed.id === "string" && parsed.id ? parsed.id : crypto.randomUUID(),
            createdAt:
              typeof parsed.createdAt === "string" && parsed.createdAt
                ? parsed.createdAt
                : new Date().toISOString(),
            stops: parsed.stops,
            tripName: typeof parsed.tripName === "string" ? parsed.tripName : undefined,
            share: isShareRef(parsed.share) ? parsed.share : undefined,
          };
          this.persist();
        }
      }
    } catch {
      this.state = newTripState();
    }
    this.emit({ kind: "load" });
  }

  all(): readonly Stop[] {
    return this.state.stops;
  }

  getTripName(): string {
    return this.state.tripName ?? "";
  }

  setTripName(name: string): void {
    this.state.tripName = name.trim() || undefined;
    this.persist();
    this.emit({ kind: "rename" });
  }

  get(id: string): Stop | undefined {
    return this.state.stops.find((s) => s.id === id);
  }

  add(input: Omit<Stop, "id">): Stop {
    const stop: Stop = { id: crypto.randomUUID(), ...input };
    this.state.stops.push(stop);
    this.persist();
    this.emit({ kind: "add", stop });
    return stop;
  }

  update(id: string, patch: Partial<Omit<Stop, "id">>): void {
    const idx = this.state.stops.findIndex((s) => s.id === id);
    if (idx === -1) return;
    const updated = { ...this.state.stops[idx], ...patch };
    this.state.stops[idx] = updated;
    this.persist();
    this.emit({ kind: "update", stop: updated });
  }

  remove(id: string): void {
    const before = this.state.stops.length;
    this.state.stops = this.state.stops.filter((s) => s.id !== id);
    if (this.state.stops.length === before) return;
    this.persist();
    this.emit({ kind: "remove", id });
  }

  reorder(fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= this.state.stops.length) return;
    if (toIndex < 0 || toIndex >= this.state.stops.length) return;
    const [moved] = this.state.stops.splice(fromIndex, 1);
    this.state.stops.splice(toIndex, 0, moved);
    if (toIndex > 0) {
      const previous = this.state.stops[toIndex - 1];
      moved.dateFrom = previous.dateTo;
      moved.arriveBy = previous.departBy;
    }
    this.persist();
    this.emit({ kind: "reorder" });
  }

  replaceAll(newStops: Stop[], tripName?: string): void {
    // A fresh id/createdAt on purpose: an import, a share link, or "new trip" is a *new*
    // trip in the library, not an edit of the current one. `share` is dropped for the same
    // reason — this content isn't ours to publish over; the next share mints a new link.
    this.state = { id: crypto.randomUUID(), createdAt: new Date().toISOString(), stops: [...newStops], tripName };
    this.persist();
    this.emit({ kind: "load" });
  }

  /** Empty trip with a fresh identity. Used by the "New trip" action. */
  newTrip(): void {
    this.replaceAll([], undefined);
  }

  /** Deep snapshot of the active trip — for mirroring into the library or opening it. */
  getState(): TripState {
    return structuredClone(this.state);
  }

  /**
   * Replace the active trip with a full snapshot from the library. Unlike `replaceAll`, this
   * restores the exact identity and `share` ref, so an opened trip keeps its editable link.
   */
  loadState(state: TripState): void {
    this.state = structuredClone(state);
    this.persist();
    this.emit({ kind: "load" });
  }

  serialize(): string {
    // Allowlisted on purpose, not `this.state`: `share.editToken` is a write capability and
    // an export is meant to be handed around. Anything secret added later stays out by default.
    const { stops, tripName } = this.state;
    return JSON.stringify({ stops, tripName }, null, 2);
  }

  getShare(): ShareRef | undefined {
    return this.state.share;
  }

  /** No `change` event: sharing isn't a trip edit, and emitting would re-trigger the auto-push. */
  setShare(share: ShareRef | undefined): void {
    this.state.share = share;
    this.persist();
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
  }

  private emit(detail: StopChangeDetail): void {
    this.dispatchEvent(new CustomEvent("change", { detail }));
  }
}

export const stops = new StopsStore();
